import { useEffect } from 'react';
import { View } from '@shared/components/View';
import { useFocusTrap } from '@shared/util/hooks';
import { getController } from '@shared/util/timeout';
import { AnchoredViewProps } from './props';
import styles from './AnchoredView.module.css';

export function AnchoredView({
  children,
  anchorName,
  className,
  style,
  onClose,
  lightDismiss = true,
  focusFirstInput = true,
  trapFocus = true,
  ...rest
}: AnchoredViewProps) {
  const popoverRef = useFocusTrap(trapFocus);
  const popoverState = lightDismiss ? 'auto' : 'manual';

  useEffect(() => {
    const el = popoverRef.current;
    el?.showPopover();

    if (focusFirstInput) {
      const firstInput = getFirstFocusableElement(el);
      requestAnimationFrame(() => {
        firstInput?.focus();
      });
    }

    const { signal, abort } = getController();
    const onToggle = (event: Event) => {
      const toggleEvent = event as ToggleEvent;
      if (toggleEvent.newState === 'closed') {
        onClose();
      }
    };

    // Stop Escape from bubbling to react-modal's document-level keydown listener.
    // The browser's native popover dismiss still fires and closes this sheet;
    // we just prevent the same keystroke from also closing the parent modal.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
      }
    };
    el?.addEventListener('toggle', onToggle, { signal });
    el?.addEventListener('keydown', onKeyDown, { signal });
    return abort;
  }, [onClose, focusFirstInput, popoverRef]);

  return (
    <View
      ref={popoverRef}
      popover={popoverState}
      tabIndex={-1}
      className={[styles.anchoredView, className]}
      style={{ positionAnchor: anchorName, ...style }}
      {...rest}
    >
      {children}
    </View>
  );
}

const TEXT_INPUT_SELECTOR = [
  'input:not(:is([disabled], [readonly], [type="hidden"], [type="file"], [type="checkbox"], [type="radio"]))',
  'textarea:not(:is([disabled], [readonly]))',
].join(', ');

function getFirstFocusableElement(
  el?: HTMLElement | null,
): HTMLInputElement | HTMLTextAreaElement | null | undefined {
  return el?.querySelector(TEXT_INPUT_SELECTOR);
}
